<template>
    <div :class="hasCustomDesign ? 'rsvp-custom-landing' : 'rsvp-landing-page'">
        <!-- Loading State -->
        <div v-if="loading" class="rsvp-loading-state">
            <b-spinner variant="primary" label="Loading..." />
            <p class="mt-3 text-muted">Loading event details...</p>
        </div>

        <!-- Expired State -->
        <div v-else-if="isExpired" class="rsvp-error-state">
            <div class="expired-card">
                <div class="expired-icon mb-3">
                    <i class="fa fa-calendar-times-o"></i>
                </div>
                <h4>Event Expired</h4>
                <p class="text-muted">This event has expired and is no longer accepting responses.</p>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="rsvp-error-state">
            <div class="mb-4">
                <i class="fa fa-exclamation-triangle text-danger" style="font-size: 3rem;"></i>
            </div>
            <h4 class="text-danger">Unable to Load Invitation</h4>
            <p class="text-muted">{{ error }}</p>
        </div>

        <!-- Custom Design Mode -->
        <div v-else-if="invitation && hasCustomDesign">
            <div ref="customHtmlContainer" v-html="renderedCustomHtml"></div>
        </div>

        <!-- Default Fallback Mode -->
        <b-container v-else-if="invitation" class="py-4">
            <b-row class="justify-content-center">
                <b-col lg="8" md="10" sm="12">
                    <div v-if="invitation.event && invitation.event.flyer_url" class="text-center mb-4">
                        <img
                            :src="invitation.event.flyer_url"
                            :alt="invitation.event.name + ' flyer'"
                            class="event-flyer-img img-fluid rounded shadow-sm"
                        />
                    </div>

                    <b-card class="shadow-sm border-0 mb-4">
                        <h1 class="event-title text-center mb-3">{{ invitation.event.name }}</h1>

                        <div class="event-meta text-center mb-4">
                            <div v-if="invitation.event.date" class="meta-item mb-2">
                                <i class="fa fa-calendar text-primary mr-2"></i>
                                <strong>{{ formattedDate }}</strong>
                                <span v-if="formattedEndDate"> &mdash; {{ formattedEndDate }}</span>
                            </div>
                            <div v-if="invitation.event.venue" class="meta-item mb-2">
                                <i class="fa fa-map-marker text-primary mr-2"></i>
                                <span>{{ invitation.event.venue }}</span>
                                <span v-if="invitation.event.address">, {{ invitation.event.address }}</span>
                            </div>
                            <div v-if="invitation.event.rsvp_deadline" class="meta-item mb-2">
                                <i class="fa fa-clock-o mr-2" :class="deadlinePassed ? 'text-danger' : 'text-warning'"></i>
                                <span v-if="deadlinePassed" class="text-danger font-weight-bold">RSVP deadline has passed ({{ formattedDeadline }})</span>
                                <span v-else class="text-warning">RSVP by {{ formattedDeadline }}</span>
                            </div>
                        </div>

                        <div v-if="invitation.event.description" class="event-description mb-4" v-html="invitation.event.description"></div>

                        <hr />

                        <div class="text-center">
                            <p class="mb-1 text-muted">You are invited as</p>
                            <h5>{{ invitation.invitee_name }}</h5>
                            <p class="text-muted small">{{ invitation.invitee_email }}</p>
                        </div>
                    </b-card>

                    <!-- Additional Fields: Display existing values + capture empty ones -->
                    <b-card v-if="hasAdditionalFields && (responseState === 'pending' || responseState === 'already_responded')" class="shadow-sm border-0 mb-4">
                        <h6 v-if="hasEditableFields && responseState === 'pending'" class="mb-3 text-muted">
                            <i class="fa fa-info-circle mr-1"></i>Please provide the following information:
                        </h6>
                        <h6 v-else-if="hasRsvpEditableFields && responseState === 'already_responded'" class="mb-3 text-muted">
                            <i class="fa fa-pencil mr-1"></i>You can update your information below:
                        </h6>
                        <PublicAdditionalFields
                            ref="additionalFields"
                            :fields="invitation.additional_fields"
                            :existing-values="existingFieldValues"
                            v-model="additionalFieldValues"
                            :disabled="responseState === 'already_responded' && !hasRsvpEditableFields"
                            theme="light"
                        />
                        <div v-if="hasRsvpEditableFields && responseState === 'already_responded'" class="text-center mt-3">
                            <b-button
                                variant="primary"
                                size="sm"
                                class="px-4"
                                :disabled="updatingFields"
                                @click="updateFields"
                            >
                                <b-spinner v-if="updatingFields" small class="mr-1" />
                                <i v-else class="fa fa-save mr-1"></i>
                                Update Information
                            </b-button>
                        </div>
                    </b-card>

                    <div v-if="responseState === 'pending' && deadlinePassed" class="text-center mb-4">
                        <b-alert show variant="warning" class="mb-0">
                            <i class="fa fa-exclamation-triangle mr-2"></i>
                            The RSVP deadline for this event has passed. You can no longer submit a response.
                        </b-alert>
                    </div>

                    <div v-else-if="responseState === 'pending'" class="text-center mb-4">
                        <h5 class="mb-3">Will you be attending?</h5>
                        <b-button
                            variant="success"
                            size="lg"
                            class="mx-2 px-5"
                            :disabled="submitting"
                            @click="respond('accepted')"
                        >
                            <b-spinner v-if="submitting && pendingResponse === 'accepted'" small class="mr-1" />
                            <i v-else class="fa fa-check mr-1"></i>
                            Accept
                        </b-button>
                        <b-button
                            variant="outline-danger"
                            size="lg"
                            class="mx-2 px-5"
                            :disabled="submitting"
                            @click="respond('declined')"
                        >
                            <b-spinner v-if="submitting && pendingResponse === 'declined'" small class="mr-1" />
                            <i v-else class="fa fa-times mr-1"></i>
                            Decline
                        </b-button>
                    </div>

                    <div v-else-if="responseState === 'accepted'">
                        <b-card class="border-success shadow-sm text-center mb-4">
                            <div class="mb-3">
                                <i class="fa fa-check-circle text-success" style="font-size: 3rem;"></i>
                            </div>
                            <h4 class="text-success">You're In!</h4>
                            <p>{{ responseResult.message || 'Your RSVP has been confirmed. We look forward to seeing you!' }}</p>

                            <div v-if="responseResult.rsvp_code" class="rsvp-code-box my-4 p-3 rounded">
                                <p class="mb-1 text-muted small">Your RSVP Code is:</p>
                                <h2 class="mb-0 font-weight-bold text-primary">{{ responseResult.rsvp_code }}</h2>
                            </div>
                            <p v-if="responseResult.rsvp_code" class="text-muted small">
                                Use this code with your email to login at the portal
                            </p>

                            <b-button
                                v-if="responseResult.portal_login_url"
                                :href="responseResult.portal_login_url"
                                variant="primary"
                                class="mt-2"
                            >
                                <i class="fa fa-sign-in mr-1"></i>
                                Go to Portal
                            </b-button>
                        </b-card>
                    </div>

                    <div v-else-if="responseState === 'declined'" class="text-center mb-4">
                        <b-card class="border-secondary shadow-sm">
                            <div class="mb-3">
                                <i class="fa fa-info-circle text-muted" style="font-size: 3rem;"></i>
                            </div>
                            <h4>Thank you for letting us know</h4>
                            <p class="text-muted">We appreciate your response. If you change your mind, you can use the same link to update your RSVP.</p>
                        </b-card>
                    </div>

                    <div v-else-if="responseState === 'already_responded'" class="text-center mb-4">
                        <b-card class="shadow-sm">
                            <div class="mb-3">
                                <i class="fa fa-info-circle text-info" style="font-size: 2.5rem;"></i>
                            </div>
                            <h5>You have already responded</h5>
                            <p class="text-muted">
                                Your current RSVP status:
                                <RSVPStatusBadge :status="invitation.rsvp_status" />
                            </p>
                        </b-card>
                    </div>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import Vue from 'vue'
import RSVPStatusBadge from '@/components/EventInvitations/RSVPStatusBadge.vue'
import PublicAdditionalFields from '@/components/EventInvitations/PublicAdditionalFields.vue'
import RSVPActionWidget from './RSVPActionWidget.vue'
import dateTimeMixins from '@/mixins/dateTimeMixins'

export default {
    name: 'RSVPLanding',
    mixins: [dateTimeMixins],
    components: { RSVPStatusBadge, PublicAdditionalFields },
    data() {
        return {
            loading: true,
            error: null,
            isExpired: false,
            invitation: null,
            submitting: false,
            pendingResponse: null,
            responseResult: {},
            responseState: 'pending',
            actionWidgetInstance: null,
            additionalFieldValues: {},
            updatingFields: false,
        }
    },
    computed: {
        token() {
            return this.$route.params.token
        },
        hasCustomDesign() {
            return !!this.invitation?.rsvp_landing_page_html
        },
        renderedCustomHtml() {
            if (!this.invitation?.rsvp_landing_page_html) return ''
            let html = this.invitation.rsvp_landing_page_html
            html = this.decodeHtmlEntities(html)
            return this.replaceTemplateVars(html)
        },
        hasAdditionalFields() {
            return Array.isArray(this.invitation?.additional_fields) && this.invitation.additional_fields.length > 0
        },
        existingFieldValues() {
            const fv = this.invitation?.field_values
            if (!fv || Array.isArray(fv)) return {}
            return fv
        },
        hasEditableFields() {
            if (!this.hasAdditionalFields) return false
            const existing = this.existingFieldValues
            return this.invitation.additional_fields.some(f => {
                if (f.editable_on_rsvp) return true
                const val = existing[f.field_name]
                return val === undefined || val === null || val === ''
            })
        },
        hasRsvpEditableFields() {
            if (!this.hasAdditionalFields) return false
            return this.invitation.additional_fields.some(f => f.editable_on_rsvp)
        },
        formattedDate() {
            return this.formatDate(this.invitation?.event?.date)
        },
        formattedEndDate() {
            if (!this.invitation?.event?.end_date) return ''
            return this.formatDate(this.invitation.event.end_date)
        },
        formattedDeadline() {
            if (!this.invitation?.event?.rsvp_deadline) return ''
            return this.formatDate(this.invitation.event.rsvp_deadline)
        },
        deadlinePassed() {
            if (!this.invitation?.event?.rsvp_deadline) return false
            return new Date(this.invitation.event.rsvp_deadline) < new Date()
        },
    },
    watch: {
        responseState(val) {
            if (this.actionWidgetInstance) this.actionWidgetInstance.responseState = val
        },
        submitting(val) {
            if (this.actionWidgetInstance) this.actionWidgetInstance.submitting = val
        },
        pendingResponse(val) {
            if (this.actionWidgetInstance) this.actionWidgetInstance.pendingResponse = val
        },
        responseResult(val) {
            if (this.actionWidgetInstance) this.actionWidgetInstance.responseResult = val
        },
    },
    async mounted() {
        await this.loadInvitation()
    },
    beforeDestroy() {
        if (this.actionWidgetInstance) {
            this.actionWidgetInstance.$destroy()
            this.actionWidgetInstance = null
        }
    },
    methods: {
        formatDate(dateStr) {
            if (!dateStr) return ''
            return this.displayStoredDateTime(dateStr)
        },
        decodeHtmlEntities(str) {
            const txt = document.createElement('textarea')
            txt.innerHTML = str
            return txt.value
        },
        replaceTemplateVars(html) {
            const inv = this.invitation
            const event = inv?.event || {}
            const vars = {
                event_name: event.name || '',
                event_date: this.formattedDate || '',
                event_end_date: this.formattedEndDate || '',
                venue: event.venue || '',
                address: event.address || '',
                event_description: event.description || '',
                rsvp_deadline: this.formattedDeadline || '',
                flyer_url: event.flyer_url || '',
                invitee_name: inv?.invitee_name || '',
                invitee_email: inv?.invitee_email || '',
                company_name: event.company_name || '',
            }
            Object.keys(vars).forEach(key => {
                html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), vars[key])
            })
            return html
        },
        mountActionWidget() {
            this.$nextTick(() => {
                const container = this.$refs.customHtmlContainer
                if (!container) return

                let marker = container.querySelector('[data-rsvp-action]')
                if (!marker) {
                    marker = document.createElement('div')
                    marker.setAttribute('data-rsvp-action', '')
                    marker.style.cssText = 'text-align:center;padding:30px 20px;'
                    container.appendChild(marker)
                }

                const ActionClass = Vue.extend(RSVPActionWidget)
                this.actionWidgetInstance = new ActionClass({
                    propsData: {
                        responseState: this.responseState,
                        submitting: this.submitting,
                        pendingResponse: this.pendingResponse,
                        responseResult: this.responseResult,
                        rsvpStatus: this.invitation?.rsvp_status || '',
                        additionalFields: this.invitation?.additional_fields || [],
                        existingValues: this.existingFieldValues,
                    },
                })
                this.actionWidgetInstance.$on('respond', (resp) => {
                    if (resp && resp.additional_fields) {
                        this.additionalFieldValues = resp.additional_fields
                        this.respond(resp.response)
                    } else {
                        this.respond(resp)
                    }
                })
                this.actionWidgetInstance.$on('update-fields', async (fields) => {
                    this.additionalFieldValues = fields
                    this.actionWidgetInstance.updatingFields = true
                    await this.updateFields()
                    this.actionWidgetInstance.updatingFields = false
                })
                this.actionWidgetInstance.$mount()
                marker.innerHTML = ''
                marker.appendChild(this.actionWidgetInstance.$el)
            })
        },
        async loadInvitation(retryCount = 0) {
            this.loading = true
            this.error = null
            try {
                const data = await this.$store.dispatch('eventInvitation/resolveToken', this.token)
                this.invitation = data
                if (data.has_responded) {
                    this.responseState = 'already_responded'
                }
                if (data.rsvp_landing_page_html) {
                    this.$nextTick(() => this.mountActionWidget())
                }
            } catch (err) {
                if (err?.response?.status === 410) {
                    this.isExpired = true
                } else if (retryCount < 1) {
                    await new Promise(r => setTimeout(r, 800))
                    return this.loadInvitation(retryCount + 1)
                } else {
                    this.error = err?.response?.data?.error?.message || 'This invitation link is invalid or has expired.'
                }
            } finally {
                this.loading = false
            }
        },
        async updateFields() {
            if (this.$refs.additionalFields) {
                const isValid = this.$refs.additionalFields.validate()
                if (!isValid) return
            }
            this.updatingFields = true
            try {
                const result = await this.$store.dispatch('eventInvitation/updateRSVPFields', {
                    token: this.token,
                    additional_fields: this.additionalFieldValues,
                })
                this.$bvToast.toast(result.message || 'Your information has been updated.', {
                    title: 'Updated',
                    variant: 'success',
                    solid: true,
                })
                if (result.field_values) {
                    this.invitation.field_values = result.field_values
                }
            } catch (err) {
                this.$bvToast.toast(
                    err?.response?.data?.error?.message || 'Failed to update. Please try again.',
                    { title: 'Error', variant: 'danger', solid: true }
                )
            } finally {
                this.updatingFields = false
            }
        },
        async respond(response) {
            if (response === 'accepted' && this.hasEditableFields && this.$refs.additionalFields) {
                const isValid = this.$refs.additionalFields.validate()
                if (!isValid) return
            }

            this.submitting = true
            this.pendingResponse = response
            try {
                const payload = {
                    token: this.token,
                    response,
                }
                if (response === 'accepted' && this.hasEditableFields) {
                    payload.additional_fields = this.additionalFieldValues
                }
                const result = await this.$store.dispatch('eventInvitation/submitRSVP', payload)
                this.responseResult = result
                this.responseState = response
            } catch (err) {
                this.$bvToast.toast(
                    err?.response?.data?.error?.message || 'Failed to submit your response. Please try again.',
                    { title: 'Error', variant: 'danger', solid: true }
                )
            } finally {
                this.submitting = false
                this.pendingResponse = null
            }
        },
    },
}
</script>

<style scoped>
.rsvp-loading-state,
.rsvp-error-state {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
}
.rsvp-custom-landing {
    min-height: 100vh;
}
.rsvp-landing-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%);
}
.event-title {
    font-size: 2rem;
    font-weight: 700;
    color: #2c3e50;
}
.event-meta .meta-item {
    font-size: 1.05rem;
}
.event-description {
    line-height: 1.7;
    color: #555;
}
.event-flyer-img {
    max-height: 400px;
    object-fit: cover;
    width: 100%;
}
.rsvp-code-box {
    background: #f0f7ff;
    border: 2px dashed #4a90d9;
}
.expired-card {
    background: #fff;
    border-radius: 16px;
    padding: 48px 40px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    max-width: 420px;
    text-align: center;
}
.expired-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e67e22, #e74c3c);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto;
}
@media (max-width: 576px) {
    .event-title {
        font-size: 1.5rem;
    }
    .rsvp-landing-page {
        padding: 0;
    }
    .expired-card {
        padding: 32px 24px;
        margin: 0 16px;
    }
}
</style>
